import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { ONBOARDING_STEPS } from './onboarding.constants';
import OnboardingSpotlight from './OnboardingSpotlight';
import OnboardingTooltip from './OnboardingTooltip';
import useOnboardingTour from '../../hooks/useOnboardingTour';
import classes from './Onboarding.module.scss';

const rectFromElement = (el) => {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

const OnboardingTour = () => {
  const { isActive, stepIndex, stepCount, next, prev, skip, getTarget } =
    useOnboardingTour();
  const [rect, setRect] = useState(null);

  const step = ONBOARDING_STEPS[stepIndex];

  const measure = useCallback(() => {
    if (!isActive || !step) return;
    const el = getTarget(step.id);
    setRect(rectFromElement(el));
  }, [isActive, step, getTarget]);

  useLayoutEffect(() => {
    if (!isActive) {
      setRect(null);
      return;
    }
    measure();
  }, [isActive, stepIndex, measure]);

  useEffect(() => {
    if (!isActive) return undefined;
    const id = window.setTimeout(measure, 50);
    return () => window.clearTimeout(id);
  }, [isActive, stepIndex, measure]);

  useEffect(() => {
    if (!isActive) return undefined;

    const handleResize = () => measure();
    const handleScroll = () => measure();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    let observer = null;
    if (typeof ResizeObserver !== 'undefined' && step) {
      const el = getTarget(step.id);
      if (el) {
        observer = new ResizeObserver(() => measure());
        observer.observe(el);
      }
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      if (observer) observer.disconnect();
    };
  }, [isActive, step, measure, getTarget]);

  useEffect(() => {
    if (!isActive) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') skip();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isActive, skip]);

  if (!isActive || !step) return null;

  return ReactDOM.createPortal(
    <>
      <div className={classes.Backdrop} onClick={skip} aria-hidden="true" />
      <OnboardingSpotlight
        rect={rect}
        padding={stepIndex === 0 ? 0 : undefined}
        borderRadius={stepIndex === 0 ? 16 : undefined}
        yOffset={10}
      />
      <OnboardingTooltip
        key={step.id}
        rect={rect}
        step={step}
        stepIndex={stepIndex}
        stepCount={stepCount}
        onSkip={skip}
        onPrev={prev}
        onNext={next}
      />
    </>,
    document.body
  );
};

export default OnboardingTour;
